import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../app/firebase';

const CACHE_KEY = 'eventTypesCache_v1';

const readCache = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.items)) return null;
    return parsed.items;
  } catch {
    return null;
  }
};

const writeCache = (items) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        items,
        savedAt: new Date().toISOString(),
      }),
    );
  } catch {
    // non‑critical
  }
};

export const fetchEventTypes = createAsyncThunk(
  'eventTypes/fetchEventTypes',
  async (_, { rejectWithValue }) => {
    if (!db) {
      return rejectWithValue('Firestore is not initialized');
    }

    try {
      const snap = await getDocs(collection(db, 'eventTypes'));
      const items = [];

      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        items.push({
          id: docSnap.id,
          key: data.key || docSnap.id,
          category: data.category || 'other',
          order: typeof data.order === 'number' ? data.order : 0,
          enabledByDefault:
            typeof data.enabledByDefault === 'boolean'
              ? data.enabledByDefault
              : true,
          iconUrl: data.iconUrl || null,
          labels: data.labels || null,
          supportsMedication: !!data.supportsMedication,
        });
      });

      // сортировка: сначала по category, внутри по order и key
      items.sort((a, b) => {
        if (a.category === b.category) {
          if (a.order === b.order) {
            return String(a.key).localeCompare(String(b.key));
          }
          return a.order - b.order;
        }
        return String(a.category).localeCompare(String(b.category));
      });

      writeCache(items);

      return items;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load event types');
    }
  },
);

const initialState = {
  items: [],
  loading: false,
  error: null,
  initializedFromCache: false,
};

const eventTypesSlice = createSlice({
  name: 'eventTypes',
  initialState,
  reducers: {
    hydrateEventTypesFromCache(state) {
      if (state.initializedFromCache) return;
      const cached = readCache();
      if (cached && Array.isArray(cached) && cached.length > 0) {
        state.items = cached;
      }
      state.initializedFromCache = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEventTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEventTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload || [];
      })
      .addCase(fetchEventTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message || 'Unknown error';
      });
  },
});

export const { hydrateEventTypesFromCache } = eventTypesSlice.actions;

export const selectEventTypesState = (state) => state.eventTypes || initialState;
export const selectEventTypes = (state) => selectEventTypesState(state).items;

export const selectEventTypesByCategory = (state, category) =>
  selectEventTypes(state).filter((et) => et.category === category);

export default eventTypesSlice.reducer;

